import React, { FC, useCallback, useEffect, useMemo } from "react";
import {
  ButtonsWrapper,
  Content,
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { useFormik } from "formik";
import { List } from "immutable";
import { OpenRightPanel } from "types/common/rightPanel";
import { createFieldBlurHandler } from "utils/formValidation";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import * as yup from "yup";
import Button from "../../../../components/Button/Button";
import Icon from "../../../../components/Icon/Icon";
import Select from "../../../../components/Select/Select";
import injectReducer from "../../../../reduxFolder/injectReducer";
import shopifyProfilesReducer, {
  getShopifyProfiles,
} from "../../../../reduxFolder/reducers/shopifyProfiles";
import shopifyItemsReducer, {
  exportToShopify,
} from "../../../../reduxFolder/reducers/shopifyItems";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  shopifyProfilesSelectItems,
  shopifyProfilesSelectLoading,
  shopifyItemsSelectExports,
} from "../../../../reduxFolder/selectors/shopifySelectors";
import {
  toastResponseError,
  toastResponseSuccess,
} from "../../../../utils/responseMessageHelper";
import {
  Message,
  Paragraph,
} from "../../../../styles/serviceRequestStyles";
import { ExportForm, Title } from "./ShopifyExportPanel.styles";

interface ShopifyExportPanelProps {
  panelData: any;
  isOpen: boolean;
  profiles: List<any>;
  isLoading: boolean;
  shopifyExports: any;
  getShopifyProfiles: () => void;
  exportToShopify: (itemId: number, profileId: number) => Promise<any>;
  openRightPanel: OpenRightPanel;
}

const ShopifyExportPanel: FC<ShopifyExportPanelProps> = React.memo(
  ({
    panelData,
    isOpen,
    profiles,
    isLoading,
    shopifyExports,
    getShopifyProfiles,
    exportToShopify,
    openRightPanel,
  }) => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const itemId = panelData.get("exportItemId") || panelData.get("id");
    const returnScreen = panelData.get("returnScreen") || "STORAGE_ITEM";
    const existingExportState = shopifyExports?.get(itemId);

    useEffect(() => {
      if (isOpen) {
        getShopifyProfiles();
      }
    }, [isOpen, getShopifyProfiles]);

    const profileOptions = useMemo(
      () =>
        profiles
          .map((profile: any) => ({
            label: profile.get("store_name"),
            value: profile.get("id"),
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label))
          .toArray(),
      [profiles],
    );

    const handleCancel = useCallback(() => {
      // Remove temporary fields and restore original panelData
      const cleanedPanelData = panelData
        .delete("exportItemId")
        .delete("returnScreen");
      openRightPanel(returnScreen, cleanedPanelData);
    }, [openRightPanel, panelData, returnScreen]);

    const handleCreateProfile = useCallback(() => {
      handleCancel();
      navigate("/shopify/profiles");
    }, [navigate, handleCancel]);

    const formik = useFormik({
      initialValues: {
        profileId: null,
      },
      validationSchema: yup.object({
        profileId: yup
          .mixed()
          .required(t("error.required", { field: t("shopify.selectProfile") })),
      }),
      onSubmit: async (values) => {
        try {
          await exportToShopify(itemId, (values.profileId as any).value);
          toastResponseSuccess(t("shopify.exportSuccess"));
          handleCancel();
        } catch (error) {
          toastResponseError(error);
        }
      },
    });
    const {
      values,
      errors,
      touched,
      isSubmitting,
      setFieldValue,
      handleSubmit,
    } = formik;
    const handleBlur = createFieldBlurHandler(formik);

    const handleChangeSelect = useCallback(
      (selectedOption: unknown, context: any) => {
        if (context.name) {
          setFieldValue(context.name, selectedOption);
        }
      },
      [setFieldValue],
    );

    return (
      <ContentWrapper>
        <Heading $isBordered>
          <button onClick={handleCancel}>
            <Icon type={IconType.Arrow} />
          </button>
          <Title>{t("shopify.exportToShopify")}</Title>
        </Heading>
        <Content>
          {profiles.size === 0 && !isLoading ? (
            <ExportForm onSubmit={(e) => e.preventDefault()}>
              <div>
                <Paragraph>{t("shopify.exportDescription")}</Paragraph>
                <Message>{t("shopify.noProfilesAvailable")}</Message>
              </div>
              <ButtonsWrapper>
                <Button onClick={handleCancel}>{t("common.cancel")}</Button>
                <Button
                  onClick={handleCreateProfile}
                  color={ButtonColor.Primary}
                  variant={ButtonVariant.Filled}
                >
                  {t("shopify.createProfile")}
                </Button>
              </ButtonsWrapper>
            </ExportForm>
          ) : (
            <ExportForm onSubmit={handleSubmit}>
              <div>
                <Paragraph>{t("shopify.exportDescription")}</Paragraph>
                {existingExportState && (
                  <Message>{t("shopify.alreadyExported")}</Message>
                )}
                {!existingExportState && (
                  <Select
                    placeholder={t("shopify.selectProfile")}
                    name="profileId"
                    value={values.profileId}
                    onChange={handleChangeSelect}
                    options={profileOptions}
                    isLoading={isLoading}
                    onBlur={handleBlur}
                    tooltip={errors.profileId as string}
                    invalid={!!errors.profileId && !!touched.profileId}
                  />
                )}
              </div>
              <ButtonsWrapper>
                <Button onClick={handleCancel}>{t("common.cancel")}</Button>
                {!existingExportState && (
                  <Button
                    type={ButtonType.Submit}
                    disabled={isSubmitting || !values.profileId}
                    isLoading={isSubmitting}
                    color={ButtonColor.Primary}
                    variant={ButtonVariant.Filled}
                  >
                    {t("shopify.requestExport")}
                  </Button>
                )}
              </ButtonsWrapper>
            </ExportForm>
          )}
        </Content>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    profiles: shopifyProfilesSelectItems,
    isLoading: shopifyProfilesSelectLoading,
    shopifyExports: shopifyItemsSelectExports,
  }),
  {
    getShopifyProfiles,
    exportToShopify,
    openRightPanel,
  },
);

const withProfilesReducer = injectReducer({
  key: "shopifyProfiles",
  reducer: shopifyProfilesReducer,
});

const withItemsReducer = injectReducer({
  key: "shopifyItems",
  reducer: shopifyItemsReducer,
});

const enhance = compose(withProfilesReducer, withItemsReducer, withConnect);

export default enhance(ShopifyExportPanel);
